"use client";

import React, { useState } from "react";
import { Star, ArrowRight, Activity, CircleCheck } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const getScore = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return 85 + Math.abs(hash) % 15;
};
function ReviewsBrowser() {
  const {
    posts,
    loading,
    error,
    setQuery,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const [activeCategory, setActiveCategory] = useState("All");
  const handleFilterChange = (val: string) => {
    if (!val) return;
    setActiveCategory(val);
    if (val === "All") {
      setQuery(prev => ({
        ...prev,
        search: undefined,
        offset: 0
      }));
    } else {
      setQuery(prev => ({
        ...prev,
        search: val,
        offset: 0
      }));
    }
  };
  return <div className="space-y-12">
      {}
      <div className="flex flex-col items-center justify-center space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Expert AI Tool Reviews
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Unbiased analysis, performance benchmarks, and feature breakdowns for the top AI software of 2026.
          </p>
        </div>

        <ToggleGroup type="single" value={activeCategory} onValueChange={handleFilterChange} className="bg-muted/50 p-1 rounded-lg border border-border">
          {["All", "Content", "Video", "Marketing"].map((category, i) => <ToggleGroupItem key={category} value={category} data-index={i} className="px-6 py-2 text-sm font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all rounded-md">
              {category}
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>

      {}
      {loading && posts.length === 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[300px] rounded-xl bg-muted/20 animate-pulse border border-border/50" />)}
        </div> : error ? <div className="text-center py-20 text-muted-foreground">
          <p>Unable to load reviews at this time.</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="mt-4">
            Try Again
          </Button>
        </div> : posts.length === 0 ? <div className="text-center py-20 text-muted-foreground">
          <p>No reviews found for this category.</p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {posts.map((post, i) => {
        const score = getScore(post.title);
        const summary = post.excerpt ? post.excerpt.replace(/<[^>]+>/g, "").split(" ").slice(0, 3).join(" ") + "..." : "AI automation tool";
        return <Card key={post.id} data-index={i} className="group relative overflow-hidden border-border/60 bg-card/50 hover:bg-card hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/5">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div className="relative h-12 w-12 overflow-hidden rounded-lg bg-background border border-border/50 p-2 flex items-center justify-center">
                    {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-contain" /> : <Activity className="h-6 w-6 text-primary" />}
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="font-mono text-2xl font-bold text-primary tracking-tighter">
                      {score}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                      Score
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-4">
                  <div>
                    <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="text-base font-medium text-muted-foreground/80">
                      {summary}
                    </CardDescription>
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    {["Fast", "Reliable", "Pro"].map((tag, idx) => <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium border border-secondary/20">
                            {tag}
                        </span>)}
                  </div>
                </CardContent>

                <CardFooter className="pt-4">
                  <Link to={post.link || "#"} className="w-full">
                    <Button className="w-full justify-between group-hover:bg-primary group-hover:text-primary-foreground transition-all" variant="secondary">
                      Read Review
                      <ArrowRight className="h-4 w-4 opacity-50 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>;
      })}
        </div>}

      {}
      {hasNext && <div className="flex justify-center pt-8">
          <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[200px] border-primary/20 hover:bg-primary/10 hover:text-primary">
            {isRefetching ? <span className="flex items-center gap-2">
                <Activity className="h-4 w-4 animate-spin" /> Loading...
              </span> : "Load More Reviews"}
          </Button>
        </div>}
    </div>;
}
export default function AllExpertReviews() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="all-expert-reviews" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <PostsProvider wp_query={wp_query}>
          <ReviewsBrowser />
        </PostsProvider>
      </div>
    </section>;
}